import React from 'react';
import { Phone, ArrowUpRight, MapPin } from 'lucide-react';
import { HERO_STATS, PHONE, PHONE_HREF, IMAGES } from '../mock';

export default function Hero() {
  return (
    <section id="top" className="relative min-h-screen pt-[72px] overflow-hidden">
      {/* Background image + gradient wash */}
      <div className="absolute inset-0">
        <img src={IMAGES.heroAlley} alt="Air conditioning units in a dim London alley at night" className="w-full h-full object-cover opacity-[0.28]" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(9,12,19,0.55) 0%, rgba(9,12,19,0.82) 55%, rgba(9,12,19,0.98) 100%)' }} />
        <div className="absolute inset-0 noise-grid opacity-40" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Top status strip */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-8 pb-10 label-mono text-[var(--muted)]" data-reveal>
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-[var(--accent)] pulse-dot" /> Live · 24/7 Callout Desk
          </span>
          <span className="hidden md:inline">Est. 2016 · F-Gas Certified</span>
          <span className="hidden md:inline">N51.5074° · W0.1278°</span>
        </div>

        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 items-start pb-16">
          {/* Left: headline */}
          <div>
            <h1 className="font-display text-[clamp(2.4rem,6.2vw,5.4rem)]" data-reveal>
              <span className="block text-white">London&rsquo;s Emergency</span>
              <span className="block text-[var(--accent)]">Response</span>
              <span className="block text-white">for Air Conditioning,</span>
              <span className="block font-display-italic" style={{ background: 'linear-gradient(100deg,#eef2f8,#8a94a8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Leak Detection &amp; Plumbing</span>
            </h1>

            <p className="mt-8 max-w-xl text-[var(--muted)] text-[15px] leading-relaxed" data-reveal data-delay="80">
              Split-system down at 2 AM. Server room climbing 34°C. A restaurant full of covers, no cold air. We keep London’s air moving — on-call engineers, dispatched within the hour, across all 33 boroughs.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-6" data-reveal data-delay="140">
              <a href={PHONE_HREF} className="btn-accent flex items-center gap-3 rounded-md px-6 py-4 label-mono tracking-[0.14em] text-[0.74rem]">
                <Phone size={15} strokeWidth={2.4} /> Call the Callout Desk <ArrowUpRight size={15} />
              </a>
              <a href="#request" className="flex items-center gap-2 label-mono text-[var(--text)] link-sweep text-[0.74rem]">
                Or Request a Callout <ArrowUpRight size={14} />
              </a>
            </div>

            {/* Stats */}
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-xl" data-reveal data-delay="200">
              {HERO_STATS.map((s) => (
                <div key={s.value} className="border-t border-[var(--line-strong)] pt-3">
                  <div className="font-display text-[clamp(1.5rem,3vw,2rem)] text-white">{s.value}</div>
                  <div className="mt-2 text-[11px] text-[var(--muted)] leading-snug">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: file image card */}
          <div className="lg:mt-16" data-reveal data-delay="120">
            <div className="relative border border-[var(--line-strong)] hover-card-lift">
              <div className="flex items-center justify-between px-4 py-2.5 label-mono text-[var(--muted)] border-b border-[var(--line)] bg-[var(--card-2)]">
                <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-[var(--accent)]" /> File · 001.LDN</span>
              </div>
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={IMAGES.heroAlley} alt="AC units alley" className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg,transparent 60%,rgba(9,12,19,0.85))' }} />
                <span className="absolute bottom-3 right-3 label-mono text-white/80">Fitzrovia · 02:14</span>
              </div>
            </div>
            <p className="mt-4 flex items-start gap-2 text-[11px] text-[var(--muted)] label-mono leading-relaxed">
              <MapPin size={13} className="text-[var(--accent)] mt-0.5 shrink-0" />
              Case log · Server room comfort restored inside 47 minutes, Fitzrovia, Feb 2025.
            </p>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="flex flex-col items-center gap-3 pb-10">
          <span className="label-mono text-[var(--muted-2)]">Scroll</span>
          <span className="w-px h-10 bg-[var(--accent)] scroll-line" />
        </div>
      </div>
    </section>
  );
}
